import React, { JSX } from "react";
import StatusLabel from "./StatusLabel";
import GlobalStyle from "../../styles/GlobalStyle";
import {
  scaleVertical,
  FontSizes,
  scaleHorizontal,
  scaleModerate,
} from "../../styles/responsive";
import { V2ReportStyle } from "../../styles/V2ReportStyle";
import { PALLETTE } from "../../utils/theme";
import { ReortLabels } from "../../utils/helpers/newReportHelper";

// Added back the required type definition
type typeSectionTypeProp =
  | "Neurological_Abnormal_Body_Tone_"
  | "Orthopedics_Spinal_Abnormalities_"
  | "Lymphatic_PLN_"
  | "Deficiencies_Suspected_Vitamins_"
  | "Xray_"
  | "Urine_Test_"
  | "Stool_Test_"
  | "Blood_Test_"
  | "Ultrasound_"
  | "Dental_Doctor_Consultation"
  | "ENT_Doctor_Consultation"
  | "Eye_Doctor_Consultation_"
  | "Refraction_"
  | "Spectacles_"
  | "Physical_Doctor_Consultation";

const SectionRenderCheckList = ({
  title,
  section = [],
}: {
  title: string;
  section?: any[];
}) => {
  return (
    <div style={{ ...V2ReportStyle.flexColumnCenter, gap: scaleVertical(4) }}>
      {title && (
        <span style={{ ...GlobalStyle.blackMediumText, fontSize: "2rem" }}>
          {title}
        </span>
      )}

      <div
        style={{
          ...V2ReportStyle.flexRowCenter,
          gap: scaleHorizontal(5),
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {section?.map((item, index) => (
          <div
            key={index}
            style={{
              ...V2ReportStyle.flexRowCenter,
              flex: 0,
              borderWidth: 1,
              borderStyle: "solid",
              padding: "5px 10px",
              whiteSpace: "nowrap", // Add this
              borderRadius: scaleModerate(10),
              borderColor: item?.isHSG
                ? PALLETTE.borderGreen
                : PALLETTE.borderBlue,
              backgroundColor: item?.isHSG ? PALLETTE.bgGreen : PALLETTE.bgBlue,
            }}
          >
            <span
              style={{
                ...GlobalStyle.blackMediumText,
                fontSize: "1.7rem",
              }}
            >
              {item?.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RenderCheckList = ({
  title,
  isHsg,
}: {
  title: string | undefined;
  isHsg?: boolean;
}) => {
  return (
    <div
      style={{
        ...V2ReportStyle.flexRowCenter,
        display: "flex",
        flex: 0,
        whiteSpace: "nowrap", // Add this
        borderWidth: 1,
        borderStyle: "solid",
        padding: "5px 10px",
        borderRadius: scaleModerate(10),
        borderColor: isHsg ? PALLETTE.borderGreen : PALLETTE.borderBlue,
        backgroundColor: isHsg ? PALLETTE.bgGreen : PALLETTE.bgBlue,
      }}
    >
      <span style={{ ...GlobalStyle.blackMediumText, fontSize: "1.7rem" }}>
        {title}
      </span>
    </div>
  );
};

const SuggestedInvestigationCard = ({
  imageSource,
  topLefticon,
  title,
  data,
  remark,
  sections = [],
  label,
}: {
  imageSource?: any;
  topLefticon?: JSX.Element | null;
  title?: string;
  suggestionText?: string;
  data: any;
  remark?: string;
  sections?: typeSectionTypeProp[];
  label: any;
}) => {
  const allKeys = Object.keys(data).filter((key) => data[key] === true);

  const uniqueKeys = allKeys.filter((key) => {
    if (key.startsWith("HSG_")) {
      const nonHsgKey = key.replace("HSG_", "");
      return !allKeys.includes(nonHsgKey);
    }
    return true;
  });

  const subData = sections
    .map((sectionKeyPrefix) => {
      const res = uniqueKeys.reduce((acc: any[], dataKey) => {
        if (sectionKeyPrefix && dataKey.includes(sectionKeyPrefix)) {
          acc.push({
            title: ReortLabels[dataKey],
            isChecked: true,
            isHSG: dataKey.startsWith("HSG_"),
          });
        }
        return acc;
      }, []);
      return res.length > 0
        ? {
            heading: ReortLabels[sectionKeyPrefix],
            section: res,
          }
        : null;
    })
    .filter(Boolean);

  const ClinicalExaminationData = uniqueKeys.reduce((acc: any[], key) => {
    if (!sections.some((prefix) => key.includes(prefix))) {
      acc.push({
        title: ReortLabels[key],
        isChecked: true,
        isHSG: key.startsWith("HSG_"),
      });
    }
    return acc;
  }, []);

  if (ClinicalExaminationData.length === 0 && !remark) return null;

  return (
    <div style={{ ...V2ReportStyle.card, gap: scaleVertical(10) }}>
      <div
        style={{
          ...V2ReportStyle.flexRowCenter,
          justifyContent: "space-between",
        }}
      >
        <div
          style={{ ...V2ReportStyle.flexRowCenter, gap: scaleHorizontal(2) }}
        >
          {topLefticon}
          <span style={{ ...GlobalStyle.blackBoldText, fontSize: "2.2rem" }}>
            {title}
          </span>
        </div>
        <StatusLabel lable={label} />
      </div>

      <div style={{ ...V2ReportStyle.flexRowCenter, gap: scaleHorizontal(10) }}>
        {imageSource !== undefined && (
          <img
            src={imageSource}
            alt="Visual"
            style={{
              width: scaleModerate(110),
              height: scaleModerate(100),
              objectFit: "contain",
            }}
          />
        )}
        <div
          style={{
            ...V2ReportStyle.card,
            backgroundColor: PALLETTE.grayOpaque,
            padding: scaleModerate(10),
            flex: 1,
            gap: scaleVertical(5),
          }}
        >
          <div
            style={{
              ...V2ReportStyle.flexRowCenter,
              gap: scaleHorizontal(5),
              flexWrap: "wrap",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {remark && <RenderCheckList title={remark} isHsg={false} />}
            {ClinicalExaminationData.map((item, index) => (
              <RenderCheckList key={index} {...item} isHsg={item?.isHSG} />
            ))}
          </div>
          {subData.map((item: any, index) => (
            <SectionRenderCheckList
              key={index}
              title={item.heading}
              section={item.section}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          ...V2ReportStyle.flexRowCenter,
          justifyContent: "space-between",
        }}
      >
        <div
          style={{ ...V2ReportStyle.flexRowCenter, gap: scaleHorizontal(5) }}
        >
          <div
            style={{
              ...V2ReportStyle.checkBox,
              borderColor: PALLETTE.borderBlue,
              backgroundColor: PALLETTE.bgBlue,
              borderStyle: "solid",
            }}
          />
          <span
            style={{
              ...GlobalStyle.blackMediumText,
              color: PALLETTE.dark,
              fontSize: "2rem",
            }}
          >
            Suggested by Doctor
          </span>
        </div>
        <div
          style={{
            ...V2ReportStyle.flexRowCenter,
            gap: scaleHorizontal(5),
            justifyContent: "flex-end",
          }}
        >
          <div
            style={{
              ...V2ReportStyle.checkBox,
              borderColor: PALLETTE.borderGreen,
              backgroundColor: PALLETTE.bgGreen,
              borderStyle: "solid",
            }}
          />
          <span
            style={{
              ...GlobalStyle.blackMediumText,
              color: PALLETTE.dark,
              fontSize: "2rem",
            }}
          >
            Suggested by HSG
          </span>
        </div>
      </div>
    </div>
  );
};

export default SuggestedInvestigationCard;
